import React from 'react';
import styled from 'styled-components';
import { FontWeightNormal, FontWeightSemiBold } from 'autogen/design-tokens/tokens';
import type { FilterOptionViewModel } from 'autogen/swagger/all';
import InlineButton from 'components/generic/InlineButton';
import useScrollToTopOfProductList from 'components/shared/productList/shared/hooks/useScrollToTopOfProductList';
import isClient from 'helpers/isClient';
import useGA4Tracking from 'modules/tracking/GA4/GA4Tracking';
import useFilters from '../../ProductListFilter/shared/hooks/useFilters';

/* #region styles */
const StyledInlineButton = styled(InlineButton)<{ $isActive: boolean }>`
    font-weight: ${({ $isActive }) => ($isActive ? FontWeightSemiBold : FontWeightNormal)};
`;
/* #endregion */

export default function PageSizeOption({ model }: { model: FilterOptionViewModel }) {
    const { displayName, isSelected: isSelectedSSRValue, queryStringName, value } = model;
    const { removeFilter, updateFilter, isFilterValueActive, isFilterActive, activeFilters } = useFilters();
    const scrollToTopOfProductList = useScrollToTopOfProductList();
    const ga4Tracking = useGA4Tracking();

    const currentActivePageSizeValue = activeFilters.get(queryStringName);

    const isDefaultPageSizeOption = !value; // if the FilterOption doesn't have a value it's the default option (aka 36).

    const handlePageSizeChange = (nextActivePageSizeValue: string) => {
        isDefaultPageSizeOption
            ? removeFilter(queryStringName)
            : updateFilter(queryStringName, nextActivePageSizeValue);
        ga4Tracking({
            eventName: 'page_size_change',
            category: 'navigation',
            eventParams: [
                { param_name: 'list_name', param_value: displayName },
                { param_name: 'page_size', param_value: nextActivePageSizeValue }
            ]
        });

        if (Number(nextActivePageSizeValue) < Number(currentActivePageSizeValue)) {
            scrollToTopOfProductList();
        }
    };

    if (isDefaultPageSizeOption) {
        const isSelected = !isFilterActive(queryStringName);
        const defaultValue = '36';

        return (
            <StyledInlineButton $isActive={isSelected} size="S" onClick={() => handlePageSizeChange(defaultValue)}>
                {displayName}
            </StyledInlineButton>
        );
    }

    const isSelectedClientValue = isFilterValueActive(queryStringName, value);
    const isSelected = isClient ? isSelectedClientValue : isSelectedSSRValue;

    return (
        <StyledInlineButton $isActive={isSelected} size="S" onClick={() => handlePageSizeChange(value)}>
            {displayName}
        </StyledInlineButton>
    );
}
